import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, Users, CalendarDays, Smartphone, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocalizedPath, getRouteByKey, type SiteLanguage } from "@/utils/routing";

import { ProcessStep } from "@/types";

const EXTRA_MODULE_SLUGS = [
  "sklad-materialy",
  "riadenie-timov",
  "kalendar-planovanie",
  "mobilna-aplikacia",
] as const;

interface ProcessModulesProps {
  steps?: ProcessStep[];
}

const ProcessModules = ({ steps }: ProcessModulesProps = {}) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "sk") as SiteLanguage;
  const [hoveredModule, setHoveredModule] = useState<number | null>(null);

  const icons = [Package, Users, CalendarDays, Smartphone];
  const colors = [
    "bg-orange-500/10 text-orange-600",
    "bg-pink-500/10 text-pink-600",
    "bg-blue-500/10 text-blue-600",
    "bg-teal-500/10 text-teal-600",
  ];
  const hoverColors = [
    "group-hover:bg-orange-500/20",
    "group-hover:bg-pink-500/20",
    "group-hover:bg-blue-500/20",
    "group-hover:bg-teal-500/20",
  ];

  const translatedModules = t("pages.home.processModules.modules", { returnObjects: true }) as Array<{
    name: string;
    description: string;
    features: string[];
  }>;

  const defaultModules = (Array.isArray(translatedModules) ? translatedModules : []).map((module, index) => ({
    icon: icons[index] || Package,
    name: module.name,
    description: module.description,
    color: colors[index % colors.length],
    hoverColor: hoverColors[index % hoverColors.length],
    features: module.features,
    href: getLocalizedPath(`/moduly/${EXTRA_MODULE_SLUGS[index] || EXTRA_MODULE_SLUGS[0]}/`, lang),
  }));

  const processModules = steps
    ? steps.map((step, index) => ({
      icon: step.icon,
      name: step.title,
      description: step.description,
      color: colors[index % colors.length],
      hoverColor: hoverColors[index % hoverColors.length],
      features: [] as string[],
      href: getRouteByKey("modules", lang),
    }))
    : defaultModules;

  const modules = processModules;

  return (
    <section className="section-padding bg-card" id="features">
      <div className="container-custom">
        <div className="text-center space-y-4 mb-8 sm:mb-10 px-4">
          <Badge variant="secondary" className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-primary/10 text-primary">
            {t("pages.home.processModules.badge")}
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl text-foreground font-bold break-words">
            {t("pages.home.processModules.title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
            {t("pages.home.processModules.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 px-4 lg:px-0">
          {modules.map((module, index) => {
            const IconComponent = module.icon;
            const isHovered = hoveredModule === index;

            return (
              <Link
                key={index}
                to={module.href}
                onMouseEnter={() => setHoveredModule(index)}
                onMouseLeave={() => setHoveredModule(null)}
                className="group"
              >
                <Card className="p-4 sm:p-6 h-full border-border bg-card/50 backdrop-blur-sm transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2 relative overflow-hidden touch-manipulation active:scale-[0.98]">
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${module.hoverColor.replace("group-hover:", "")}`} />

                  <div className="relative z-10 space-y-4">
                    <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-lg ${module.color} flex items-center justify-center transition-all duration-300 group-hover:scale-110`}>
                      <IconComponent className="w-5 sm:w-6 h-5 sm:h-6" />
                    </div>

                    <div className="space-y-3 min-w-0">
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <h3 className="text-base sm:text-lg text-card-foreground font-semibold break-words min-w-0">{module.name}</h3>
                        <ArrowRight className={`w-4 h-4 shrink-0 text-primary transition-all duration-200 ${isHovered ? "opacity-100 translate-x-1" : "opacity-0"}`} />
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {module.description}
                      </p>

                      <div className={`space-y-2 overflow-hidden transition-all duration-300 ${isHovered ? "opacity-100 max-h-40" : "opacity-0 max-h-0"}`}>
                        {module.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center text-xs text-muted-foreground">
                            <div className="w-1 h-1 bg-primary rounded-full mr-2" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 sm:mt-10 flex justify-center px-4">
          <Button asChild variant="outline" className="whitespace-normal h-auto py-2">
            <Link to={getRouteByKey("modules", lang)} data-testid="process-modules-view-all">
              {t("pages.home.processModules.viewAll")}
              <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProcessModules;
